import { useLiveQuery } from 'dexie-react-hooks'
import { useSyncExternalStore } from 'react'

import db from '../db'
import { DexieDownload, DownloadState } from '../db/types'
import { activeDownload, ActiveDownload, subscribeToDownloads } from './active'
import { PROJECT_KEY } from './downloader'
import { offlineSupported, remove, retry, startCollectionDownload } from './index'

/** What a download looks like to the surface showing it. `none` is a tour that was never downloaded. */
export type DownloadStatus = DownloadState | 'none'

export interface DownloadView {
	status: DownloadStatus
	/** How far the run in flight has got. Absent unless one is running. */
	progress?: ActiveDownload['progress']
	/** The language the row was built for; a tour saved in another language is not saved in this one. */
	language?: string
	/** False when the browser offers no service worker or Cache API — an in-app browser, a private window. */
	supported: boolean
	/**
	 * Whether the registry has been read. Until it has, `status` reads `none` for a tour that turns out to
	 * be saved, so anything that acts on `none` rather than merely drawing it has to wait for this.
	 */
	loaded: boolean
	start: () => void
	cancel: () => void
	retryNow: () => void
	removeNow: () => void
	prioritizeStops: (stopIds: number[]) => void
}

const noop = () => {}

const useActiveDownload = (key: string): ActiveDownload | undefined =>
	useSyncExternalStore(
		subscribeToDownloads,
		() => activeDownload(key),
		() => undefined,
	)

// `null` is the query still running, which `undefined` cannot say: that is also a key with no row.
const useRow = (key: string): DexieDownload | null | undefined => useLiveQuery(() => db.downloads.get(key), [key], null)

/**
 * The registry row is the source of truth, so what a visitor sees survives navigating away and back —
 * leaving the tour page does not cancel the download, and returning finds it rather than starting another.
 */
export const useCollectionDownload = (collectionId: number, title?: string): DownloadView => {
	const key = `collection:${collectionId}`
	const row = useRow(key)
	const running = useActiveDownload(key)

	return {
		// A row exists from before the first byte lands, but only the live download knows it is still going.
		status: running ? 'downloading' : (row?.state ?? 'none'),
		progress: running?.progress,
		language: row?.language,
		supported: offlineSupported(),
		loaded: row !== null,
		start: () => void startCollectionDownload(collectionId, title),
		cancel: running?.cancel ?? noop,
		retryNow: () => void retry(key),
		removeNow: () => void remove(key),
		prioritizeStops: running?.prioritizeStops ?? noop,
	}
}

/**
 * The project-wide pre-download, only while it is running. Nothing is shown for one that has finished:
 * it was never asked for, so it reports itself and then gets out of the way.
 */
export const useProjectDownload = (): ActiveDownload | undefined => useActiveDownload(PROJECT_KEY)

/**
 * The tours saved in the language being read. A tour saved in another language is not on this list: every
 * URL it stored is that language's, so offline it has nothing to show.
 */
export const useDownloadedCollections = (language: string): Set<number> => {
	// `useLiveQuery` resolves asynchronously, so it answers `undefined` on the first render whatever
	// initial value it is given, and answers it for the whole render when the hook itself is stubbed.
	const rows = useLiveQuery(() => db.downloads.where('state').equals('complete').toArray(), [], [])

	return new Set(
		(rows ?? [])
			.filter(row => row.language === language && row.key.startsWith('collection:'))
			.map(row => Number(row.key.slice('collection:'.length)))
			.filter(id => Number.isFinite(id)),
	)
}
